import type { RefObject } from 'react';
import { Link } from 'react-router-dom';
import type { ChronumSite } from '@shared/api';
import { Button } from '@shared/ui/Button';
import { FleetChip } from '@entities/fleet';
import {
  bindingShort,
  siteNeedsAttention,
  siteState,
  siteStateHint,
  siteStateLabel,
  siteStateTone,
} from '@entities/chronum';
import { SaveErrorNotice, type SaveError } from '../MappingParts';
import { SiteEditor, type SiteChange } from '../SiteEditor';
import { CellMeta } from '../ui';
import {
  CenterLine,
  NarrowLabel,
  RowError,
} from './styles';

export function SiteRow({
  site,
  canWrite,
  progressAccess,
  isEditing,
  busy,
  error,
  rereading,
  suggestions,
  knownCodes,
  removers,
  triggers,
  rowHeads,
  inputRef,
  commentRef,
  onSaveSite,
  onRemoveCostCenter,
  onStartEdit,
  onCancelEdit,
  onReread,
}: {
  site: ChronumSite;
  canWrite: boolean;
  progressAccess: boolean;
  isEditing: boolean;
  busy: boolean;
  error: SaveError | null;
  rereading: boolean;
  suggestions: readonly string[];
  knownCodes: readonly string[];
  removers: RefObject<Map<string, HTMLButtonElement>>;
  triggers: RefObject<Map<string, HTMLButtonElement>>;
  rowHeads: RefObject<Map<string, HTMLTableCellElement>>;
  inputRef: RefObject<HTMLInputElement | null>;
  commentRef: RefObject<HTMLTextAreaElement | null>;
  onSaveSite: (site: ChronumSite, change: SiteChange) => void;
  onRemoveCostCenter: (site: ChronumSite) => void;
  onStartEdit: (siteId: string) => void;
  onCancelEdit: (siteId: string) => void;
  onReread: (siteId: string) => void;
}) {
  const state = siteState(site);
  const hint = siteStateHint(site);
  const bound = site.projects.length > 0;

  return (
    <>
      <tr
        className="row"
        data-state={state}
        data-attention={siteNeedsAttention(site) ? 'true' : undefined}
        data-editing={isEditing ? 'true' : undefined}
      >
        <th
          scope="row"
          className="c-name"
          tabIndex={-1}
          ref={(el) => {
            if (rowHeads.current) {
              if (el) rowHeads.current.set(site.id, el);
              else rowHeads.current.delete(site.id);
            }
          }}
        >
          <span className="name">{site.name}</span>
          {!site.listed && (
            <CellMeta>
              <FleetChip
                $tone="muted"
                title="Площадка есть в справочнике Экономиста, но её нет в последнем списке Хронума"
              >
                нет в последнем списке Хронума
              </FleetChip>
            </CellMeta>
          )}
          {site.comment && (
            <CellMeta className="comment">{`Комментарий: ${site.comment}`}</CellMeta>
          )}
        </th>
        <td className={`c-projects${bound ? '' : ' empty'}`}>
          <NarrowLabel className="narrow-only">Стройка (у Руководителя)</NarrowLabel>
          {!bound
            ? '—'
            : site.projects.map((p) => (
                <span className="project" key={p.id}>
                  {progressAccess ? (
                    <Link
                      to={`/agents/progress?object=${encodeURIComponent(p.id)}`}
                      title="Профиль объекта у Руководителя"
                    >
                      {p.name}
                    </Link>
                  ) : (
                    p.name
                  )}
                  <span className="how">{bindingShort(p.binding)}</span>
                </span>
              ))}
        </td>
        <td className="c-center">
          <NarrowLabel className="narrow-only">Центр затрат</NarrowLabel>
          <CenterLine>
            {site.cost_center_code ? (
              <span className="code">{site.cost_center_code}</span>
            ) : (
              <span>—</span>
            )}
            {canWrite && !isEditing && state === 'conflict' && (
              <Button
                ref={(el) => {
                  if (removers.current) {
                    if (el) removers.current.set(site.id, el);
                    else removers.current.delete(site.id);
                  }
                }}
                type="button"
                $variant="secondary"
                $size="sm"
                disabled={busy}
                aria-label={
                  busy ? undefined : `Снять центр затрат у площадки «${site.name}»`
                }
                onClick={() => onRemoveCostCenter(site)}
              >
                {busy ? 'Снимаю…' : 'Снять центр затрат'}
              </Button>
            )}
            {!isEditing &&
              (canWrite && !bound ? (
                <Button
                  ref={(el) => {
                    if (triggers.current) {
                      if (el) triggers.current.set(site.id, el);
                      else triggers.current.delete(site.id);
                    }
                  }}
                  type="button"
                  $variant={siteNeedsAttention(site) ? 'secondary' : 'ghost'}
                  $size="sm"
                  disabled={busy}
                  aria-label={
                    site.cost_center_code
                      ? `Изменить центр затрат и комментарий площадки «${site.name}»`
                      : `Указать центр затрат площадке «${site.name}»`
                  }
                  onClick={() => onStartEdit(site.id)}
                >
                  {site.cost_center_code ? 'Изменить' : 'Указать центр затрат'}
                </Button>
              ) : (
                <Button
                  ref={(el) => {
                    if (triggers.current) {
                      if (el) triggers.current.set(site.id, el);
                      else triggers.current.delete(site.id);
                    }
                  }}
                  type="button"
                  $variant="ghost"
                  $size="sm"
                  aria-label={`Комментарий и журнал правок площадки «${site.name}»`}
                  onClick={() => onStartEdit(site.id)}
                >
                  {canWrite ? 'Комментарий и журнал' : 'Журнал правок'}
                </Button>
              ))}
          </CenterLine>
          {error && !isEditing && (
            <RowError>
              <SaveErrorNotice
                error={error}
                busy={rereading}
                onReread={() => onReread(site.id)}
              />
            </RowError>
          )}
        </td>
        <td className="c-state">
          <FleetChip $tone={siteStateTone(state)}>{siteStateLabel(state)}</FleetChip>
          {hint && <CellMeta>{hint}</CellMeta>}
        </td>
      </tr>
      {isEditing && (
        <tr className="editor">
          <td colSpan={4}>
            <SiteEditor
              site={site}
              canWrite={canWrite}
              canSetCenter={!bound}
              suggestions={suggestions}
              knownCodes={knownCodes}
              busy={busy}
              error={error}
              rereading={rereading}
              inputRef={inputRef}
              commentRef={commentRef}
              onSave={(change) => onSaveSite(site, change)}
              onRemove={() => onRemoveCostCenter(site)}
              onCancel={() => onCancelEdit(site.id)}
              onReread={() => onReread(site.id)}
            />
          </td>
        </tr>
      )}
    </>
  );
}
